import { useCallback, useEffect, useState } from 'react';
import { ScriptState } from '@/pages/widgetEditor/types';

const ENTRY_SELECTOR = 'script[data-widget-entry]';

// The entry only logs a chunk that fails to load (blocked, 404, bad deploy) —
// no event reaches this page — so a tag still undefined after this long is
// reported as failed rather than left spinning.
export const WIDGET_LOAD_TIMEOUT_MS = 15_000;

/**
 * Loads the selected version's module entry and reports, per tag, whether the
 * custom element is usable yet.
 *
 * The entry fetches a widget's chunk only once it finds the tag on the page, so
 * readiness is `customElements.whenDefined`, not the script's `load` event —
 * and the preview must render the elements before they are defined, or nothing
 * would ever be discovered.
 *
 * Module scripts are fetched in CORS mode, so this relies on the widgets
 * distribution sending `access-control-allow-origin`, unlike the classic
 * scripts it replaces.
 */
export const useWidgetEntry = (
    entryUrl: string | undefined,
    tags: string[]
): ((tag: string) => ScriptState) => {
    const [failure, setFailure] = useState<{
        url: string;
        state: 'error' | 'stale';
    } | null>(null);
    const [, setDefinedCount] = useState(0);
    const [timedOutKey, setTimedOutKey] = useState<string | null>(null);

    const needsEntry = Boolean(entryUrl) && tags.length > 0;

    useEffect(() => {
        if (!entryUrl || !needsEntry) return;

        let script =
            document.head.querySelector<HTMLScriptElement>(ENTRY_SELECTOR);

        // Element definitions are page-global and permanent: once one
        // version's entry has registered the tags, another version's cannot
        // replace them without a reload.
        if (script && script.dataset.widgetEntry !== entryUrl) {
            setFailure({ url: entryUrl, state: 'stale' });
            return;
        }

        // A remount must not wait forever for an error event that already fired.
        if (script?.dataset.failed) {
            setFailure({ url: entryUrl, state: 'error' });
            return;
        }

        if (!script) {
            script = document.createElement('script');
            script.type = 'module';
            script.src = entryUrl;
            script.dataset.widgetEntry = entryUrl;
            document.head.appendChild(script);
        }

        const entryScript = script;
        const handleError = (error: unknown) => {
            console.error(error);
            entryScript.dataset.failed = 'true';
            setFailure({ url: entryUrl, state: 'error' });
        };
        entryScript.addEventListener('error', handleError);
        return () => entryScript.removeEventListener('error', handleError);
    }, [entryUrl, needsEntry]);

    const tagKey = Array.from(new Set(tags)).sort().join(' ');

    useEffect(() => {
        let cancelled = false;
        tagKey
            .split(' ')
            .filter((tag) => tag && !customElements.get(tag))
            .forEach((tag) =>
                customElements.whenDefined(tag).then(() => {
                    if (!cancelled) setDefinedCount((count) => count + 1);
                })
            );
        return () => {
            cancelled = true;
        };
    }, [tagKey]);

    // One timer for the whole selection, restarted whenever it changes.
    useEffect(() => {
        if (!needsEntry) return;
        const timer = setTimeout(
            () => setTimedOutKey(`${entryUrl} ${tagKey}`),
            WIDGET_LOAD_TIMEOUT_MS
        );
        return () => clearTimeout(timer);
    }, [entryUrl, needsEntry, tagKey]);

    return useCallback(
        (tag: string): ScriptState => {
            if (failure && failure.url === entryUrl) return failure.state;
            if (customElements.get(tag)) return 'ready';
            return timedOutKey === `${entryUrl} ${tagKey}`
                ? 'error'
                : 'loading';
        },
        [failure, entryUrl, timedOutKey, tagKey]
    );
};
